import _toConsumableArray from '@babel/runtime/helpers/toConsumableArray';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import { getJSON, getCSRFHeaders } from '@folklore/fetch';
import get from 'lodash-es/get';
import isArray from 'lodash-es/isArray';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearch } from 'wouter';
import Select from '@panneau/element-select';

var _excluded = ["onChange", "options", "requestUrl", "requestOptions", "requestQuery", "requestParams", "itemValuePath", "itemLabelPath", "maxItemsCount", "paginated", "clearValue", "autoSize", "className"];
var propTypes = {
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    value: PropTypes.any // eslint-disable-line react/forbid-prop-types
  })),
  requestUrl: PropTypes.string,
  requestOptions: PropTypes.object,
  // eslint-disable-line react/forbid-prop-types
  requestQuery: PropTypes.object,
  // eslint-disable-line react/forbid-prop-types
  requestParams: PropTypes.object,
  // eslint-disable-line react/forbid-prop-types
  itemValuePath: PropTypes.string,
  itemLabelPath: PropTypes.string,
  maxItemsCount: PropTypes.number,
  paginated: PropTypes.bool,
  clearValue: PropTypes.any,
  // eslint-disable-line react/forbid-prop-types
  autoSize: PropTypes.bool,
  className: PropTypes.string
};
var defaultProps = {
  options: null,
  requestUrl: null,
  requestOptions: null,
  requestQuery: null,
  requestParams: null,
  itemValuePath: null,
  itemLabelPath: null,
  maxItemsCount: null,
  paginated: false,
  clearValue: null,
  autoSize: false,
  className: null
};
var SelectFilter = function SelectFilter(_ref) {
  var onChange = _ref.onChange,
    initialOptions = _ref.options,
    requestUrl = _ref.requestUrl,
    requestOptions = _ref.requestOptions,
    requestQuery = _ref.requestQuery,
    requestParams = _ref.requestParams,
    itemValuePath = _ref.itemValuePath,
    itemLabelPath = _ref.itemLabelPath,
    maxItemsCount = _ref.maxItemsCount,
    paginated = _ref.paginated,
    clearValue = _ref.clearValue,
    autoSize = _ref.autoSize,
    className = _ref.className,
    props = _objectWithoutProperties(_ref, _excluded);
  var _useState = useState(initialOptions || null),
    _useState2 = _slicedToArray(_useState, 2),
    options = _useState2[0],
    setOptions = _useState2[1];
  var _useState3 = useState(null),
    _useState4 = _slicedToArray(_useState3, 2),
    loading = _useState4[0],
    setLoading = _useState4[1];
  var _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    endReached = _useState6[0],
    setEndReached = _useState6[1];
  var _useState7 = useState(null),
    _useState8 = _slicedToArray(_useState7, 2),
    pagination = _useState8[0],
    setPagination = _useState8[1];
  useEffect(function () {
    setOptions(initialOptions || null);
  }, [initialOptions, setOptions]);
  var search = useSearch();
  var _useState9 = useState(1),
    _useState10 = _slicedToArray(_useState9, 2),
    page = _useState10[0],
    setPage = _useState10[1];
  var query = useMemo(function () {
    return _objectSpread(_objectSpread({}, paginated ? {
      page: page
    } : null), queryString.parse(search, {
      arrayFormat: 'bracket'
    }));
  }, [search, page, paginated]);
  var finalParams = useMemo(function () {
    var currentQuery = query || {};
    var currentParams = requestParams || [];
    return Object.keys(currentQuery).reduce(function (obj, name) {
      var inParams = currentParams.find(function (p) {
        return p === name;
      }) || null;
      if (inParams !== null && currentQuery[name]) {
        return _objectSpread(_objectSpread({}, obj), {}, _defineProperty({}, name, currentQuery[name]));
      }
      if (paginated && (name === 'page' || name === 'count')) {
        return _objectSpread(_objectSpread({}, obj), {}, _defineProperty({}, name, currentQuery[name]));
      }
      return obj;
    }, {});
  }, [query, requestParams]);
  var fetchOptions = useCallback(function (url) {
    if (!endReached && url !== null) {
      setLoading(true);
      var partialQuery = _objectSpread(_objectSpread({
        paginated: paginated
      }, requestQuery), finalParams);
      var finalQuery = queryString.stringify(partialQuery, {
        arrayFormat: 'bracket'
      });
      getJSON("".concat(url).concat(finalQuery !== null && finalQuery.length > 0 ? "?".concat(finalQuery) : ''), _objectSpread({
        credentials: 'include',
        headers: getCSRFHeaders()
      }, requestOptions)).then(function (newItems) {
        var partialItems = newItems !== null && !isArray(newItems) && typeof newItems.data !== 'undefined' ? newItems.data || [] : newItems;
        var finalItems = maxItemsCount !== null ? partialItems.slice(0, maxItemsCount) : partialItems;
        if (paginated) {
          var oldPagination = newItems !== null && !isArray(newItems) && typeof newItems.meta !== 'undefined' ? newItems.meta || {} : null;
          var newPagination = newItems !== null && !isArray(newItems) && typeof newItems.pagination !== 'undefined' ? newItems.pagination || {} : null;
          setOptions([].concat(_toConsumableArray(options || []), _toConsumableArray((finalItems || []).map(function (it) {
            return {
              label: get(it, itemLabelPath, null),
              value: get(it, itemValuePath, null)
            };
          }))));
          setPagination(newPagination || oldPagination);
        } else {
          setOptions((finalItems || []).map(function (it) {
            return {
              label: get(it, itemLabelPath, null),
              value: get(it, itemValuePath, null)
            };
          }));
          setPagination(null);
        }
        setLoading(false);
      })["catch"](function () {
        setOptions(initialOptions);
        setPagination(null);
        setLoading(false);
      });
    }
  }, [options, initialOptions, maxItemsCount, requestQuery, requestOptions, finalParams, itemLabelPath, itemValuePath, paginated, setOptions, setPagination, setLoading, endReached]);
  useEffect(function () {
    fetchOptions(requestUrl);
  }, [requestUrl, finalParams]);
  useEffect(function () {
    if (!paginated) {
      setPagination(null);
    }
  }, [paginated]);
  var onMenuScrollToBottom = useCallback(function () {
    if (!loading && paginated && pagination !== null) {
      var _ref2 = pagination || {},
        paginationPage = _ref2.page,
        lastPage = _ref2.last_page;
      if (paginationPage < lastPage) {
        setPage(paginationPage + 1);
      } else {
        setEndReached(true);
      }
    }
  }, [loading, page, setPage, paginated, pagination, setEndReached]);
  var finalOnChange = useCallback(function (val) {
    var newValue = isArray(val) && val.length === 0 ? null : val;
    if (onChange !== null) {
      onChange(newValue);
    }
  }, [onChange]);
  return /*#__PURE__*/React.createElement(Select, Object.assign({
    autoSize: autoSize
  }, props, {
    onChange: finalOnChange,
    clearValue: clearValue,
    className: className,
    options: options || [],
    onMenuScrollToBottom: paginated ? onMenuScrollToBottom : null
  }));
};
SelectFilter.propTypes = propTypes;
SelectFilter.defaultProps = defaultProps;

export { SelectFilter as default };
